import React, { Suspense, useMemo, useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import {
  Grid,
  OrbitControls,
  GizmoHelper,
  GizmoViewport,
  useGLTF,
  Edges,
  TransformControls,
  Html ,
} from "@react-three/drei";
import DrillingPreviewMesh from "../mounting-editor/DrillingPreviewMesh";
import GeneratedAssetMesh from "../asset-geometry/GeneratedAssetMesh";

const PUBLIC_BASE_URL = "https://modularobjects.com";

function evaluateFormula(value, context = {}, fallback = 0) {
  if (typeof value === "number") return value;

  const raw = String(value ?? "").trim();
  if (!raw) return fallback;

  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return numeric;

  try {
    const names = Object.keys(context || {}).filter((key) =>
      /^[A-Za-z_][A-Za-z0-9_]*$/.test(key)
    );

    const values = names.map((key) => Number(context[key]) || 0);

    // Dozvoljavamo samo matematički izraz nad parametrima iz context-a.
    // Ovo je za interni editor, ali ipak ograničavamo imena parametara.
    const fn = new Function(...names, `"use strict"; return (${raw});`);
    const result = fn(...values);

    return toNumber(result, fallback);
  } catch (err) {
    console.warn("Failed to evaluate formula:", raw, err);
    return fallback;
  }
}

function resolveValue(value, context = {}, fallback = 0) {
  return evaluateFormula(value, context, fallback);
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function degToRad(value) {
  return (toNumber(value) * Math.PI) / 180;
}

function OriginMarker({
  label = "Origin",
  size = 80,
  color = "#ef4444",
  showPlane = false,
}) {
  return (
    <group>
      <axesHelper args={[size]} />

      <mesh>
        <sphereGeometry args={[6, 24, 24]} />
        <meshStandardMaterial color={color} />
      </mesh>

      {showPlane ? (
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <planeGeometry args={[size * 2, size * 2]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={0.08}
            side={THREE.DoubleSide}
          />
        </mesh>
      ) : null}

      <Html distanceFactor={12}>
        <div className="px-2 py-1 rounded bg-white border text-[11px] whitespace-nowrap shadow">
          {label}
        </div>
      </Html>
    </group>
  );
}

function DebugTransformLabel({ label, position, rotation }) {
  return (
    <Html distanceFactor={12}>
      <div className="px-2 py-1 rounded bg-white/95 border text-[10px] font-mono whitespace-nowrap shadow">
        <div className="font-semibold">{label}</div>
        <div>
          P: {Number(position?.[0] || 0).toFixed(1)},{" "}
          {Number(position?.[1] || 0).toFixed(1)},{" "}
          {Number(position?.[2] || 0).toFixed(1)}
        </div>
        <div>
          R: {Number(rotation?.[0] || 0).toFixed(2)},{" "}
          {Number(rotation?.[1] || 0).toFixed(2)},{" "}
          {Number(rotation?.[2] || 0).toFixed(2)}
        </div>
      </div>
    </Html>
  );
}

function getItemAssemblyFrame() {
  return {
    position: [0, 0, 0],
    rotation: [0, 0, 0],
  };
}

function getAssetGlbPath(asset) {
  return (
    asset?.converted_file_path ||
    asset?.convertedFilePath ||
    asset?.converted_file ||
    asset?.convertedFile ||
    asset?.file_url ||
    ""
  );
}

function toAbsoluteUrl(path) {
  if (!path) return "";
  const str = String(path);

  if (str.startsWith("http")) return str;
  if (str.startsWith("/")) return `${PUBLIC_BASE_URL}${str}`;

  return `${PUBLIC_BASE_URL}/${str}`;
}

function normalizeMeta(component) {
  const meta = component?.metadata_json || {};

  return {
    enabled: meta.enabled !== false,
    position: {
      x: meta.position?.x ?? 0,
      y: meta.position?.y ?? 0,
      z: meta.position?.z ?? 0,
    },
    rotation: {
      x: meta.rotation?.x ?? 0,
      y: meta.rotation?.y ?? 0,
      z: meta.rotation?.z ?? 0,
    },
    parameters: meta.parameters || {},
  };
}

function normalizeAssetMounting(component) {
  const mounting = component?.asset_mounting || {};

  const ref = mounting.reference_frame_json || {};
  const role = String(component?.asset_role || "").toLowerCase();

  let rotationSource = ref.defaultRotation || { x: 0, y: 0, z: 0 };

  if (role.includes(".right") && ref.rightRotation) {
    rotationSource = ref.rightRotation;
  }

  if (role.includes(".left") && ref.leftRotation) {
    rotationSource = ref.leftRotation;
  }

  return {
    offset: {
      x: ref.offset?.x ?? 0,
      y: ref.offset?.y ?? 0,
      z: ref.offset?.z ?? 0,
    },
    rotation: {
      x: rotationSource?.x ?? 0,
      y: rotationSource?.y ?? 0,
      z: rotationSource?.z ?? 0,
    },
    drillings:
      mounting.drilling_pattern_json?.drillings ||
      mounting.drilling_pattern_json?.holes ||
      [],
    items:
      mounting.items ||
      component?.mounting_items ||
      [],
  };
}

function normalizeComponentItemMounting(component) {
  const mounting =
    component?.component_item?.item_mounting ||
    component?.component_item?.itemMounting ||
    component?.item_mounting ||
    {};

  const ref = mounting.reference_frame_json || {};

  return {
    offset: {
      x: ref.offset?.x ?? 0,
      y: ref.offset?.y ?? 0,
      z: ref.offset?.z ?? 0,
    },
    rotation: {
      x: ref.defaultRotation?.x ?? 0,
      y: ref.defaultRotation?.y ?? 0,
      z: ref.defaultRotation?.z ?? 0,
    },

    drillings:
      mounting.drilling_pattern_json?.drillings ||
      mounting.drilling_pattern_json?.holes ||
      mounting.drillings ||
      [],

    items:
      mounting.items ||
      component?.component_item?.mounting_items ||
      component?.item_mounting_items ||
      [],
  };
}

function GlbPrimitive({ url }) {
  const { scene } = useGLTF(url);

  const cloned = useMemo(() => {
    const obj = scene.clone(true);

    // VAŽNO:
    // Ne centriramo GLB automatski.
    // Origin mora ostati isti kao u asset/mounting sistemu.
    obj.position.set(0, 0, 0);
    obj.scale.set(1, 1, 1);

    obj.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.material = new THREE.MeshStandardMaterial({
          color: child.material?.color || "#d1d5db",
          roughness: 0.55,
          metalness: 0.1,
        });
      }
    });

    return obj;
  }, [scene]);

  return <primitive object={cloned} />;
}

function ComponentMesh({
  component,
  selected,
  onSelect,
  registerRef,
  showDrillings,
  showOrigins = true,
  assemblyParameters = {},
}) {
  const meta = normalizeMeta(component);
  const assetMounting = normalizeAssetMounting(component);
  const componentItemMounting = normalizeComponentItemMounting(component);
  const context = {
  ...(assemblyParameters || {}),
  ...(meta.parameters || {}),
};

const groupRef = useRef(null);

useEffect(() => {
  if (!meta.enabled) {
    registerRef?.(component.id, null);
    return;
  }

  registerRef?.(component.id, groupRef.current);

  return () => registerRef?.(component.id, null);
}, [component.id, registerRef, meta.enabled]);

if (!meta.enabled) return null;

const asset = component.asset;
const assetMetadata = readJson(asset?.metadata_json, {});
const generatedGeometry = assetMetadata.geometry || null;

const glbPath = getAssetGlbPath(asset);
const glbUrl = toAbsoluteUrl(glbPath);

if (!glbUrl && !generatedGeometry) return null;

  const position = [
  resolveValue(meta.position.x, context, 0),
  resolveValue(meta.position.y, context, 0),
  resolveValue(meta.position.z, context, 0),
];

  const rotation = [
  degToRad(resolveValue(meta.rotation.x, context, 0)),
  degToRad(resolveValue(meta.rotation.y, context, 0)),
  degToRad(resolveValue(meta.rotation.z, context, 0)),
];

  const mountingOffset = [
    toNumber(assetMounting.offset.x),
    toNumber(assetMounting.offset.y),
    toNumber(assetMounting.offset.z),
  ];

  const mountingRotation = [
    degToRad(assetMounting.rotation.x),
    degToRad(assetMounting.rotation.y),
    degToRad(assetMounting.rotation.z),
  ];

  const componentItemOffset = [
  toNumber(componentItemMounting.offset.x),
  toNumber(componentItemMounting.offset.y),
  toNumber(componentItemMounting.offset.z),
];

const componentItemRotation = [
  degToRad(componentItemMounting.rotation.x),
  degToRad(componentItemMounting.rotation.y),
  degToRad(componentItemMounting.rotation.z),
];

  const drillings = Array.isArray(assetMounting.drillings)
  ? assetMounting.drillings
  : [];

  const componentItemDrillings = Array.isArray(componentItemMounting.drillings)
  ? componentItemMounting.drillings
  : [];

const componentItemMountingItems = Array.isArray(componentItemMounting.items)
  ? componentItemMounting.items
  : [];

  return (
  <group
  ref={groupRef}
  position={position}
  rotation={rotation}
  onClick={(e) => {
    e.stopPropagation();
    onSelect?.(component);
  }}
>
  {showOrigins ? (
    <>
      <OriginMarker
        label={`COMPONENT / ITEM MOUNTING ORIGIN · ${component.asset_role || component.id}`}
        size={80}
        color="#2563eb"
      />

      <DebugTransformLabel
        label="component origin local"
        position={position}
        rotation={rotation}
      />
    </>
  ) : null}

  {/* ITEM MOUNTING DATA:
      Ovo je ono što si dodao u Item Mounting Editoru.
      NE ide kroz componentItemOffset, jer bi dobilo GLB/model korekciju.
  */}
  {showDrillings
    ? componentItemDrillings
        .filter((drill) => drill?.enabled !== false)
        .map((drill, index) => (
          <DrillingPreviewMesh
            key={drill.id || `${component.id}_component_item_drill_${index}`}
            drill={drill}
          />
        ))
    : null}

  {showDrillings
    ? componentItemMountingItems
        .filter((item) => item?.status !== "deleted")
        .map((item) => (
          <MountingHardwareMesh
            key={`component_item_mounting_item_${item.id}`}
            mountingItem={item}
          />
        ))
    : null}

  {/* MODEL / ASSET CHAIN:
      Ovde ide GLB i asset-level mounting.
  */}
  <group position={componentItemOffset} rotation={componentItemRotation}>
    {showOrigins ? (
      <OriginMarker
        label="COMPONENT ITEM MODEL OFFSET"
        size={70}
        color="#22c55e"
      />
    ) : null}

    <group position={mountingOffset} rotation={mountingRotation}>
      {showOrigins ? (
        <OriginMarker
          label="ASSET MOUNTING"
          size={60}
          color="#a855f7"
        />
      ) : null}

      {generatedGeometry ? (
        <GeneratedAssetMesh geometry={generatedGeometry} />
      ) : glbUrl ? (
        <Suspense fallback={null}>
          <GlbPrimitive url={glbUrl} />
        </Suspense>
      ) : null}

      {showDrillings
        ? drillings
            .filter((drill) => drill?.enabled !== false)
            .map((drill, index) => (
              <DrillingPreviewMesh
                key={drill.id || `${component.id}_asset_drill_${index}`}
                drill={drill}
              />
            ))
        : null}

      {showDrillings
        ? (assetMounting.items || [])
            .filter((item) => item?.status !== "deleted")
            .map((item) => (
              <MountingHardwareMesh
                key={`asset_mounting_item_${item.id}`}
                mountingItem={item}
              />
            ))
        : null}
    </group>
  </group>

  {selected ? (
    <>
      <axesHelper args={[120]} />
      <mesh>
        <boxGeometry args={[120, 120, 120]} />
        <meshBasicMaterial transparent opacity={0} />
        <Edges color="#2563eb" />
      </mesh>
    </>
  ) : null}
</group>
);
}

function getMountingFrameTransform(mounting) {
  const ref = mounting?.reference_frame_json || {};

  return {
    offset: [
      toNumber(ref.offset?.x, 0),
      toNumber(ref.offset?.y, 0),
      toNumber(ref.offset?.z, 0),
    ],
    rotation: [
      degToRad(ref.defaultRotation?.x || 0),
      degToRad(ref.defaultRotation?.y || 0),
      degToRad(ref.defaultRotation?.z || 0),
    ],
  };
}

function readJson(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function MountingHardwareMesh({ mountingItem }) {
  const asset =
  mountingItem?.asset ||
  mountingItem?.linked_asset ||
  mountingItem?.linkedAsset ||
  mountingItem?.item?.asset ||
  mountingItem?.item?.linked_asset ||
  null;
  const metadata = readJson(asset?.metadata_json, {});
const geometry = metadata.geometry || null;
  const glbPath = getAssetGlbPath(asset);
const glbUrl = toAbsoluteUrl(glbPath);

  const basePosition = mountingItem?.position_json || { x: 0, y: 0, z: 0 };
  const baseRotation = mountingItem?.rotation_json || { x: 0, y: 0, z: 0 };

  const instances = Array.isArray(mountingItem?.parameters_json?.instances)
    ? mountingItem.parameters_json.instances
    : [
        {
          id: "default",
          position: basePosition,
          rotation: baseRotation,
        },
      ];

  const label =
    mountingItem?.item?.code ||
    mountingItem?.item?.name ||
    asset?.code ||
    asset?.name ||
    `Item ${mountingItem?.item_id || ""}`;

  return (
    <>
      {instances.map((instance, index) => {
        const p = instance.position || basePosition;
        const r = instance.rotation || baseRotation;

        const position = [
          Number(p.x) || 0,
          Number(p.y) || 0,
          Number(p.z) || 0,
        ];

        const rotation = [
          degToRad(Number(r.x) || 0),
          degToRad(Number(r.y) || 0),
          degToRad(Number(r.z) || 0),
        ];

        return (
          <group
            key={instance.id || `${mountingItem.id}_${index}`}
            position={position}
            rotation={rotation}
          >
            {geometry ? (
  <GeneratedAssetMesh geometry={geometry} />
) : glbUrl ? (
  <Suspense fallback={null}>
    <GlbPrimitive url={glbUrl} />
  </Suspense>
) : (
  <mesh>
    <sphereGeometry args={[5, 24, 24]} />
    <meshStandardMaterial color="#f97316" />
  </mesh>
)}

            <Html distanceFactor={12}>
              <div className="px-2 py-1 rounded bg-white border text-xs whitespace-nowrap">
                {label}
              </div>
            </Html>
          </group>
        );
      })}
    </>
  );
}

function AssemblySceneContent({
  itemMounting,
  itemMountingItems = [],
  components,
  selectedComponentId,
  onSelectComponent,
  onTransformComponent,
  transformMode,
  showDrillings,
  showOrigins = true,
  assemblyParameters = {},
}) {
  const refs = useRef(new Map());
  const controlsRef = useRef(null);
  const [, forceUpdate] = useState(0);
  const itemFrame = getItemAssemblyFrame();
  const itemMountingFrame = getMountingFrameTransform(itemMounting);

  function registerRef(id, ref) {
    if (ref) {
      refs.current.set(String(id), ref);
    } else {
      refs.current.delete(String(id));
    }

    forceUpdate((x) => x + 1);
  }

  const selectedObject = selectedComponentId
    ? refs.current.get(String(selectedComponentId))
    : null;

  function handleObjectChange() {
    if (!selectedObject || !selectedComponentId) return;

    const p = selectedObject.position;
    const r = selectedObject.rotation;

    onTransformComponent?.(selectedComponentId, {
      position: {
        x: Number(p.x.toFixed(3)),
        y: Number(p.y.toFixed(3)),
        z: Number(p.z.toFixed(3)),
      },
      rotation: {
        x: Number(THREE.MathUtils.radToDeg(r.x).toFixed(3)),
        y: Number(THREE.MathUtils.radToDeg(r.y).toFixed(3)),
        z: Number(THREE.MathUtils.radToDeg(r.z).toFixed(3)),
      },
    });
  }

  return (
    <>
      <color attach="background" args={["#f3f4f6"]} />
      <ambientLight intensity={1.2} />
      <directionalLight position={[2000, 3000, 2000]} intensity={1.1} />

      <Grid
        args={[6000, 6000]}
        cellSize={50}
        sectionSize={250}
        fadeDistance={12000}
        infiniteGrid
      />

      {showOrigins ? (
  <>
    <OriginMarker
      label="GLOBAL ORIGIN 0,0,0"
      size={150}
      color="#ef4444"
      showPlane
    />

    <group position={itemFrame.position} rotation={itemFrame.rotation}>
      <OriginMarker
  label="ITEM ASSEMBLY ORIGIN 0,0,0"
  size={120}
  color="#22c55e"
/>

      <DebugTransformLabel
        label="item frame"
        position={itemFrame.position}
        rotation={itemFrame.rotation}
      />
    </group>
  </>
) : null}

      <group position={itemFrame.position} rotation={itemFrame.rotation}>
  {components.map((component) => (
    <ComponentMesh
      key={component.id}
      component={component}
      selected={String(component.id) === String(selectedComponentId)}
      onSelect={onSelectComponent}
      registerRef={registerRef}
      showDrillings={showDrillings}
      showOrigins={showOrigins}
      assemblyParameters={assemblyParameters}
    />
  ))}

  {showDrillings ? (
  <group
    position={itemMountingFrame.offset}
    rotation={itemMountingFrame.rotation}
  >
    {showOrigins ? (
      <OriginMarker
        label="ITEM MOUNTING FRAME"
        size={80}
        color="#f97316"
      />
    ) : null}

    {itemMountingItems
      .filter((item) => item?.status !== "deleted")
      .map((item) => (
        <MountingHardwareMesh
          key={`item_mounting_item_${item.id}`}
          mountingItem={item}
        />
      ))}
  </group>
) : null}
</group>

      {selectedObject ? (
        <TransformControls
          object={selectedObject}
          mode={transformMode}
          space="local"
          translationSnap={null}
rotationSnap={null}
          onMouseDown={() => {
            if (controlsRef.current) controlsRef.current.enabled = false;
          }}
          onMouseUp={() => {
            if (controlsRef.current) controlsRef.current.enabled = true;
          }}
          onObjectChange={handleObjectChange}
        />
      ) : null}

      <OrbitControls
  ref={controlsRef}
  makeDefault
  enableDamping={false}
  dampingFactor={0}
  rotateSpeed={0.65}
  zoomSpeed={0.8}
  panSpeed={0.8}
/>

      <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
        <GizmoViewport />
      </GizmoHelper>
    </>
  );
}

export default function ItemAssemblyPreview3D({
  itemMounting,
  itemMountingItems = [],
  components = [],
  selectedComponentId,
  onSelectComponent,
  onTransformComponent,
  fullHeight = false,
  transformMode = "translate",
  showDrillings = false,
  showOrigins = true,
  assemblyParameters = {},
}) {
  return (
    <div
  className={[
    "w-full border rounded overflow-hidden bg-slate-100",
    fullHeight ? "h-full min-h-[600px]" : "h-[420px]",
  ].join(" ")}
>
      <Canvas
        shadows
        camera={{
          position: [800, 600, 800],
          fov: 35,
          near: 1,
          far: 20000,
        }}
      >
        <AssemblySceneContent
  itemMounting={itemMounting}
  itemMountingItems={itemMountingItems}
  components={components}
  selectedComponentId={selectedComponentId}
  onSelectComponent={onSelectComponent}
  onTransformComponent={onTransformComponent}
  transformMode={transformMode}
  showDrillings={showDrillings}
  showOrigins={showOrigins}
  assemblyParameters={assemblyParameters}
/>
      </Canvas>
    </div>
  );
}